import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import RingLoader from 'react-spinners/RingLoader';
import {
  sendChatMessage,
  getChatSession,
  startNewChatSession,
  stopChatSession,
  getAiSuggestions,
  uploadChatAttachment,
} from '../../../services/aiComposer';
import { getUserAccessToken } from '../../../services/userSession';
import { prepareImageFileForUpload } from '../../../utils/creativeAspectRatio';
import AiChatTaskList, { inferClientTasks } from './AiChatTaskList';
import AiActivityDots from './AiActivityDots';
import './AiComposer.css';
import ComposerSmsSettings from './ComposerSmsSettings';

const SESSION_STORAGE_KEY = 'invertAiChatSessionId';
const MAX_ATTACHMENTS = 4;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

function getPreliminaryTaskMeta(messages) {
  if (!Array.isArray(messages) || !messages.length) return null;
  const last = messages[messages.length - 1];
  if (last.role === 'assistant' && last.metadata?.preliminary && last.metadata?.taskPlan?.tasks?.length) {
    return last.metadata;
  }
  return null;
}

function normalizeTaskPlanForDisplay(taskPlan, { live = false } = {}) {
  if (!taskPlan?.tasks?.length) return taskPlan;
  if (live) return taskPlan;
  return {
    ...taskPlan,
    tasks: taskPlan.tasks.map((task) => {
      if (task.status === 'in_progress' || task.status === 'pending') {
        return { ...task, status: 'completed' };
      }
      return task;
    }),
  };
}

function shouldShowHistoricalTaskList(metadata) {
  if (!metadata?.taskPlan?.tasks?.length) return false;
  if (metadata.preliminary || metadata.stopped) return true;
  if (metadata.multiTask || metadata.taskPlan.tasks.length > 1) return true;
  return metadata.taskPlan.tasks.some(
    (task) => task.status === 'failed' || task.status === 'cancelled',
  );
}

function markFirstPendingInProgress(taskPlan) {
  if (!taskPlan?.tasks?.length) return taskPlan;
  let found = false;
  return {
    ...taskPlan,
    tasks: taskPlan.tasks.map((t) => {
      if (!found && t.status === 'pending') {
        found = true;
        return { ...t, status: 'in_progress' };
      }
      return t;
    }),
  };
}
function isPreliminaryPending(messages) {
  if (!Array.isArray(messages) || !messages.length) return false;
  const last = messages[messages.length - 1];
  return last.role === 'assistant' && Boolean(last.metadata?.preliminary);
}

function isImageUrl(url) {
  return /\.(png|jpe?g|webp|gif)(\?|$)/i.test(url || '');
}

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const base64 = typeof dataUrl === 'string' && dataUrl.includes(',')
        ? dataUrl.split(',')[1]
        : dataUrl;
      resolve(base64);
    };
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

function MediaPreview({ url, alt }) {
  if (!url) return null;
  if (isImageUrl(url)) {
    return <img src={url} alt={alt} className="aiChatMedia" />;
  }
  return (
    <video src={url} autoPlay loop muted playsInline className="aiChatMedia" />
  );
}

function AttachmentThumbnails({ attachments, onRemove, readOnly = false }) {
  if (!attachments?.length) return null;
  return (
    <div className="aiChatAttachmentRow">
      {attachments.map((att, i) => (
        <div key={att.url || att.previewUrl || i} className="aiChatAttachmentThumb">
          <img src={att.previewUrl || att.url} alt={att.filename || 'Attachment'} />
          {!readOnly && onRemove && (
            <button
              type="button"
              className="aiChatAttachmentRemove"
              onClick={() => onRemove(i)}
              aria-label="Remove attachment"
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

const markdownComponents = {
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
};

function ChatMessageContent({ role, content, attachments }) {
  if (!content && !attachments?.length) return null;

  if (role === 'assistant') {
    return (
      <div className="aiChatMarkdown">
        {content ? (
          <ReactMarkdown components={markdownComponents}>{content}</ReactMarkdown>
        ) : null}
      </div>
    );
  }

  return (
    <>
      {attachments?.length > 0 && (
        <AttachmentThumbnails attachments={attachments} readOnly />
      )}
      {content ? <p className="aiChatMsgText">{content}</p> : null}
    </>
  );
}

export default function AiComposer() {
  const token = getUserAccessToken();
  const [input, setInput] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [optimisticTaskPlan, setOptimisticTaskPlan] = useState(null);
  const threadRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const stoppedRef = useRef(false);

  const scrollToBottom = useCallback((force = false) => {
    const el = threadRef.current;
    if (!el) return;
    if (force || isNearBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, []);

  const handleThreadScroll = useCallback(() => {
    const el = threadRef.current;
    if (!el) return;
    const threshold = 96;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  useEffect(() => {
    scrollToBottom(false);
  }, [messages, loading, generating, scrollToBottom]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const startPolling = useCallback((activeSessionId) => {
    if (!token || !activeSessionId) return;
    stopPolling();

    const pollSession = async () => {
      try {
        const res = await getChatSession(token, activeSessionId);
        const data = res.data;
        setMessages(data.messages || []);
        const stillProcessing = data.status === 'processing'
          || isPreliminaryPending(data.messages);
        if (!stillProcessing) {
          stopPolling();
          setLoading(false);
          setGenerating(false);
          setOptimisticTaskPlan(null);
        }
      } catch {
        stopPolling();
        setLoading(false);
        setGenerating(false);
      }
    };

    pollSession();
    pollRef.current = setInterval(pollSession, 2000);
  }, [token, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const loadSuggestions = useCallback(async () => {
    if (!token) return;
    try {
      const res = await getAiSuggestions(token);
      setSuggestions(res.data.suggestions || []);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    loadSuggestions();
  }, [loadSuggestions]);

  useEffect(() => {
    if (!token) {
      setInitializing(false);
      return;
    }

    const savedSessionId = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!savedSessionId) {
      setInitializing(false);
      return;
    }

    getChatSession(token, savedSessionId)
      .then((res) => {
        setSessionId(res.data.sessionId);
        setMessages(res.data.messages || []);
        if (res.data.status === 'processing' || isPreliminaryPending(res.data.messages)) {
          setLoading(true);
          setGenerating(true);
          startPolling(res.data.sessionId);
        }
      })
      .catch(() => {
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      })
      .finally(() => setInitializing(false));
  }, [token, startPolling]);

  const applyChatResponse = (data) => {
    if (stoppedRef.current) return;
    if (data.sessionId) {
      setSessionId(data.sessionId);
      sessionStorage.setItem(SESSION_STORAGE_KEY, data.sessionId);
    }
    setMessages(data.messages || []);
    if (data.status === 'processing' || isPreliminaryPending(data.messages)) {
      setGenerating(true);
      setLoading(true);
      startPolling(data.sessionId);
    } else {
      setLoading(false);
      setGenerating(false);
      setOptimisticTaskPlan(null);
      stopPolling();
    }
  };

  const adjustTextareaHeight = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [input]);

  const handleNewChat = async () => {
    if (!token) return;
    stoppedRef.current = false;
    stopPolling();
    setLoading(false);
    setGenerating(false);
    setInput('');
    setAttachments([]);
    setMessages([]);
    setSessionId(null);
    setOptimisticTaskPlan(null);
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    loadSuggestions();

    try {
      const res = await startNewChatSession(token);
      setSessionId(res.data.sessionId);
      sessionStorage.setItem(SESSION_STORAGE_KEY, res.data.sessionId);
    } catch {
      /* session created on first message */
    }

    textareaRef.current?.focus();
  };

  const handleRemoveAttachment = (index) => {
    setAttachments((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length || !token) return;

    const remaining = MAX_ATTACHMENTS - attachments.length;
    if (remaining <= 0) return;

    setUploading(true);
    try {
      const toUpload = files.slice(0, remaining);
      const uploaded = await Promise.all(toUpload.map(async (file) => {
        if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
          throw new Error('Only JPEG, PNG, WebP, and GIF images are supported.');
        }
        const { file: prepared } = await prepareImageFileForUpload(file);
        const base64 = await readFileAsBase64(prepared);
        const res = await uploadChatAttachment(token, {
          content: base64,
          filename: prepared.name,
          mimeType: prepared.type,
        });
        return {
          url: res.data.url,
          filename: res.data.filename || prepared.name,
          previewUrl: URL.createObjectURL(prepared),
        };
      }));
      setAttachments((prev) => [...prev, ...uploaded]);
    } catch (err) {
      const errMsg = err.response?.data?.error || err.message || 'Upload failed';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: errMsg },
      ]);
    } finally {
      setUploading(false);
      textareaRef.current?.focus();
    }
  };

  const handleStop = async () => {
    if (!token || !sessionId || stopping) return;
    stoppedRef.current = true;
    setStopping(true);
    stopPolling();
    try {
      const res = await stopChatSession(token, sessionId);
      setMessages(res.data.messages || []);
      setLoading(false);
      setGenerating(false);
      setOptimisticTaskPlan(null);
    } catch {
      setLoading(false);
      setGenerating(false);
      setOptimisticTaskPlan(null);
    } finally {
      setStopping(false);
      textareaRef.current?.focus();
    }
  };

  const handleSubmit = async (text) => {
    const value = (text || input).trim();
    if ((!value && !attachments.length) || !token || loading || uploading) return;

    stoppedRef.current = false;
    setLoading(true);
    setInput('');
    isNearBottomRef.current = true;
    const sentAttachments = attachments.map(({ url, filename, previewUrl }) => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      return { url, filename };
    });
    setAttachments([]);
    const clientPlan = value ? inferClientTasks(value) : null;
    if (clientPlan?.tasks?.length) {
      setOptimisticTaskPlan(markFirstPendingInProgress(clientPlan));
    } else {
      setOptimisticTaskPlan(null);
    }
    setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: value,
        metadata: sentAttachments.length ? { attachments: sentAttachments } : null,
      },
    ]);

    try {
      const res = await sendChatMessage(token, {
        sessionId,
        message: value,
        attachments: sentAttachments,
      });
      applyChatResponse(res.data);
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Something went wrong. Try again.';
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: errMsg },
      ]);
      setLoading(false);
      setGenerating(false);
      setOptimisticTaskPlan(null);
      stopPolling();
    } finally {
      textareaRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    const text = suggestion.PromptTemplate || suggestion.SuggestionText;
    handleSubmit(text);
  };

  const hasStartedChat = messages.length > 0 || loading || generating;
  const lastMessage = messages[messages.length - 1];
  const hasPreliminaryMessage = lastMessage?.role === 'assistant' && lastMessage?.metadata?.preliminary;
  const preliminaryMeta = getPreliminaryTaskMeta(messages);
  const isWorking = loading || generating;
  const displayTaskPlan = isWorking
    ? (optimisticTaskPlan || preliminaryMeta?.taskPlan || null)
    : null;
  const showLeaveNotice = Boolean(
    isWorking
    && (preliminaryMeta?.leaveNotice || displayTaskPlan?.multiTask || (displayTaskPlan?.tasks?.length || 0) > 1),
  );
  const canSend = (input.trim() || attachments.length) && !loading && !uploading;

  if (initializing) {
    return (
      <div className="aiChat aiChat--loading">
        <RingLoader color="#dc5024" size={36} />
      </div>
    );
  }

  return (
    <div className="aiChat">
      <div className="aiChatTopBar">
        <ComposerSmsSettings token={token} />
        <button type="button" className="aiChatNewBtn" onClick={handleNewChat}>
          + New chat
        </button>
      </div>

      <div className="aiChatThread" ref={threadRef} onScroll={handleThreadScroll}>
        {!hasStartedChat && (
          <div className="aiChatWelcome">
            <h1>What would you like to display?</h1>
            <p>
              Describe a promotion, add weather, sports, or trivia to your screens, or upload an image as a reference.
            </p>
          </div>
        )}

        {messages.map((msg, i) => {
          const isLivePreliminary = Boolean(
            isWorking
            && i === messages.length - 1
            && msg.metadata?.preliminary,
          );
          const historicalTaskPlan = shouldShowHistoricalTaskList(msg.metadata)
            ? normalizeTaskPlanForDisplay(msg.metadata.taskPlan)
            : null;

          return (
          <div
            key={`${msg.role}-${i}-${msg.createdAt || i}`}
            className={`aiChatMsg aiChatMsg--${msg.role}${isLivePreliminary ? ' aiChatMsg--pending' : ''}`}
          >
            <div className="aiChatMsgInner">
              <ChatMessageContent
                role={msg.role}
                content={msg.content}
                attachments={msg.metadata?.attachments}
              />
              {historicalTaskPlan && !isLivePreliminary && (
                <AiChatTaskList
                  taskPlan={historicalTaskPlan}
                  active={false}
                  showLeaveNotice={false}
                />
              )}
              {isLivePreliminary && msg.metadata?.taskPlan?.tasks?.length > 0 && (
                <AiChatTaskList
                  taskPlan={msg.metadata.taskPlan}
                  active
                  showLeaveNotice={Boolean(msg.metadata.leaveNotice)}
                />
              )}
              {msg.metadata?.previewUrl && (
                <MediaPreview url={msg.metadata.previewUrl} alt="Generated signage" />
              )}
            </div>
          </div>
          );
        })}

        {loading && !generating && displayTaskPlan?.tasks?.length > 0 && (
          <div className="aiChatMsg aiChatMsg--assistant aiChatMsg--thinking">
            <div className="aiChatMsgInner aiChatThinkingPanel">
              <p className="aiChatThinkingLine">
                Understanding your request
                <AiActivityDots />
              </p>
              <AiChatTaskList
                taskPlan={displayTaskPlan}
                active
                showLeaveNotice={showLeaveNotice}
              />
            </div>
          </div>
        )}

        {loading && !generating && !displayTaskPlan?.tasks?.length && (
          <div className="aiChatMsg aiChatMsg--assistant aiChatMsg--thinking">
            <div className="aiChatMsgInner aiChatThinkingPanel">
              <p className="aiChatThinkingLine">
                Understanding your request
                <AiActivityDots />
              </p>
            </div>
          </div>
        )}

        {generating && !hasPreliminaryMessage && displayTaskPlan?.tasks?.length > 0 && (
          <div className="aiChatMsg aiChatMsg--assistant aiChatMsg--thinking">
            <div className="aiChatMsgInner aiChatThinkingPanel">
              <p className="aiChatThinkingLine">
                Working on it
                <AiActivityDots />
              </p>
              <AiChatTaskList
                taskPlan={displayTaskPlan}
                active
                showLeaveNotice={showLeaveNotice}
              />
            </div>
          </div>
        )}

        {generating && !hasPreliminaryMessage && !displayTaskPlan?.tasks?.length && (
          <div className="aiChatMsg aiChatMsg--assistant aiChatMsg--thinking">
            <div className="aiChatMsgInner aiChatThinkingPanel">
              <p className="aiChatThinkingLine">
                Working on it
                <AiActivityDots />
              </p>
            </div>
          </div>
        )}

      </div>

      <div className="aiChatInputArea">
        <div className={`aiChatSuggestions ${hasStartedChat ? 'aiChatSuggestions--hidden' : ''}`}>
          {suggestions.slice(0, 4).map((s) => (
            <button
              key={s.SuggestionId}
              type="button"
              className="aiChatSuggestion"
              onClick={() => handleSuggestionClick(s)}
              disabled={loading}
            >
              {s.SuggestionText}
            </button>
          ))}
        </div>

        <div className="aiChatInputBox" data-onboarding-id="compose-input">
          {(attachments.length > 0 || uploading) && (
            <div className="aiChatInputAttachments">
              <AttachmentThumbnails
                attachments={attachments}
                onRemove={handleRemoveAttachment}
              />
              {uploading && (
                <div className="aiChatUploadingHint">
                  <RingLoader color="#dc5024" size={14} />
                  <span>Uploading...</span>
                </div>
              )}
            </div>
          )}
          <div className="aiChatInputRow">
            <button
              type="button"
              className="aiChatAttachBtn"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading || uploading || attachments.length >= MAX_ATTACHMENTS}
              aria-label="Upload image"
              title={attachments.length >= MAX_ATTACHMENTS ? `Maximum ${MAX_ATTACHMENTS} images` : 'Upload image'}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M21 19V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
                  fill="currentColor"
                />
              </svg>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              multiple
              className="aiChatFileInput"
              onChange={handleFileSelect}
            />
            <textarea
              ref={textareaRef}
              className="aiChatTextarea"
              placeholder="Message INVERT..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={loading}
            />
            <button
              type="button"
              className={isWorking ? 'aiChatStopBtn' : 'aiChatSendBtn'}
              onClick={isWorking ? handleStop : () => handleSubmit()}
              disabled={isWorking ? (stopping || !sessionId) : !canSend}
              aria-label={isWorking ? 'Stop generating' : 'Send message'}
            >
              {isWorking ? (
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <rect x="6" y="6" width="12" height="12" rx="1" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z"
                    fill="currentColor"
                    transform="rotate(-90 12 12)"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
